import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Download, ShieldCheck, AlertTriangle } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const fmtDT = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}  ${p(dt.getHours())}:${p(dt.getMinutes())}`;
};
const toRange = (s) => (s || "").replace(/\s*[-–]\s*(?=[\d.])/, " To ");

function classify(result, range) {
  const num = parseFloat(result);
  if (isNaN(num) || !range) return "";
  const m = range.match(/([\d.]+)\s*(?:[-–]|to)\s*([\d.]+)/i);
  if (m) return num < parseFloat(m[1]) ? "L" : num > parseFloat(m[2]) ? "H" : "";
  const lt = range.match(/<\s*([\d.]+)/);
  if (lt && num >= parseFloat(lt[1])) return "H";
  const gt = range.match(/>\s*([\d.]+)/);
  if (gt && num <= parseFloat(gt[1])) return "L";
  return "";
}
const FLAG_CODE = { High: "H", Low: "L", Critical: "C", Abnormal: "A" };

function buildRows(r) {
  const p = r.patient || {};
  const sex = (p.gender || "").toLowerCase();
  const tmpl = {};
  (r.template?.parameters || []).forEach((t) => { tmpl[(t.name || "").toLowerCase()] = t; });
  return (r.lab_results || []).map((row) => {
    const name = row.test || row.parameter || "";
    const t = tmpl[name.toLowerCase()] || {};
    const range = row.ref_range || (sex.startsWith("m") && t.ref_range_male) || (sex.startsWith("f") && t.ref_range_female) || t.ref_range || "";
    const result = row.result ?? row.value ?? "";
    return {
      name, result, range: toRange(range), unit: row.unit || t.unit || "",
      group: row.group || t.group || "", method: t.method || "",
      flag: FLAG_CODE[row.flag] || classify(result, range),
    };
  });
}

export default function PublicLabReport() {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");
  const [combined, setCombined] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/api/public/lab-report/${token}`)
      .then(({ data }) => {
        setReport(data);
        document.title = `Lab Report · ${data?.patient?.name || "Patient"} · ${data?.hospital?.name || ""}`;
        if (new URLSearchParams(window.location.search).get("print") === "1") setTimeout(() => window.print(), 900);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Unable to load report"));
  }, [token]);

  if (err) return <ReportError msg={err} />;
  if (!report) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Loading report…</div>;

  const h = report.hospital || {};
  const all = report.sections?.length ? report.sections : [report];
  const sections = combined ? all : all.filter((s) => s.id === report.id);
  const pageMargin = h.lab_preprinted ? `${h.lab_top_margin_mm}mm 10mm ${h.lab_bottom_margin_mm}mm 10mm` : "6mm 10mm 8mm 10mm";

  return (
    <div className="min-h-screen bg-slate-100 py-6 print:py-0 print:bg-white">
      <Toolbar r={report} count={all.length} combined={combined} setCombined={setCombined} />
      <div data-testid="lab-report-sheet" className="lab-sheet bg-white mx-auto shadow-sm print:shadow-none text-black"
        style={{ width: "210mm", minHeight: "297mm", padding: "6mm 10mm 10mm", fontFamily: "Tahoma, Verdana, Arial, sans-serif" }}>
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead><tr><td>
            <div className={h.lab_preprinted ? "print:hidden" : ""}><Letterhead h={h} /></div>
            <MetaBlock r={report} sections={sections} token={token} />
          </td></tr></thead>
          <tbody><tr><td className="align-top">
            {sections.map((s, i) => (
              <Results key={s.id} s={s} patient={report.patient}
                showHeading={i === 0 || (s.category || "") !== (sections[i - 1].category || "") || !s.category} />
            ))}
          </td></tr></tbody>
          <tfoot><tr><td><SignatureFooter r={report} /></td></tr></tfoot>
        </table>
      </div>
      <style>{`
        .lab-row, .lab-narr { break-inside: avoid; }
        .lab-head { break-after: avoid; }
        @media print {
          @page { size: A4; margin: ${pageMargin}; }
          body, html { margin: 0; padding: 0; background: white; }
          .lab-sheet { box-shadow: none !important; margin: 0 !important; padding: 0 !important; width: auto !important; min-height: 0 !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
    </div>
  );
}

function ReportError({ msg }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="bg-white border rounded-md p-8 max-w-md text-center" data-testid="lab-report-error">
        <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
        <h2 className="text-lg font-semibold text-slate-800 mb-1">Report Unavailable</h2>
        <p className="text-sm text-slate-600">{msg}</p>
        <p className="text-xs text-slate-400 mt-4">If you believe this is a mistake, please contact the hospital directly.</p>
      </div>
    </div>
  );
}

function Toolbar({ r, count, combined, setCombined }) {
  const share = () => {
    const txt = `My ${r.name} report from ${r.hospital?.name || "the hospital"} is ready:\n\n${window.location.href.split("?")[0]}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener,noreferrer");
  };
  return (
    <div className="max-w-[210mm] mx-auto mb-3 px-2 flex items-center justify-between gap-2 print:hidden flex-wrap">
      <span className="text-xs text-slate-500 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-700" /> Verified Report · Secure Link</span>
      {count > 1 && (
        <div className="flex border border-slate-300 rounded-sm overflow-hidden text-xs" data-testid="lab-report-combine-toggle">
          <button onClick={() => setCombined(true)} data-testid="lab-report-combined" className={`px-3 h-8 ${combined ? "bg-slate-800 text-white" : "bg-white"}`}>All {count} tests</button>
          <button onClick={() => setCombined(false)} data-testid="lab-report-single" className={`px-3 h-8 ${!combined ? "bg-slate-800 text-white" : "bg-white"}`}>{r.name} only</button>
        </div>
      )}
      <div className="flex gap-2">
        <button data-testid="lab-report-whatsapp" onClick={share} className="h-9 px-3 bg-[#25D366] hover:bg-[#1EBE5A] text-white text-xs font-semibold rounded-sm">WhatsApp</button>
        <button data-testid="lab-report-print" onClick={() => window.print()} className="h-9 px-3 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-medium rounded-sm flex items-center gap-1"><Printer className="w-3.5 h-3.5" /> Print / Save as PDF</button>
        <button data-testid="lab-report-download" onClick={() => window.print()} className="h-9 px-3 border border-slate-300 bg-white hover:bg-slate-50 text-xs font-medium rounded-sm flex items-center gap-1"><Download className="w-3.5 h-3.5" /> Download</button>
      </div>
    </div>
  );
}

function Letterhead({ h }) {
  if (h.lab_header_url) {
    return <img src={`${API_BASE}${h.lab_header_url}`} alt={h.name} className="w-full" style={{ height: "auto" }} data-testid="lab-report-header-img" />;
  }
  return (
    <div className="flex items-center gap-3 pb-2" data-testid="lab-report-header-text">
      <img src={`${API_BASE}${h.logo_url}`} alt="" className="h-16 w-16 object-contain" />
      <div>
        <div className="text-[22px] font-bold leading-tight">{h.name}</div>
        {h.tagline && <div className="text-[11px] italic">{h.tagline}</div>}
        <div className="text-[10px]">{h.address}{h.phone ? ` · ${h.phone}` : ""}</div>
      </div>
    </div>
  );
}

function MetaBlock({ r, sections, token }) {
  const p = r.patient || {};
  const qr = `${window.location.origin}/lab/report/${token}`;
  const reportAt = sections.map((s) => s.verified_at || s.finalized_at).filter(Boolean).sort().pop() || r.verified_at || r.finalized_at;
  const sampleTypes = [...new Set(sections.map((s) => (s.template?.sample_type || "").toUpperCase()).filter(Boolean))].join(", ");
  return (
    <div className="mt-4 text-[11px]" data-testid="lab-report-meta">
      <div className="border-t-2 border-black text-center font-bold text-[13px] py-0.5">LABORATORY TEST REPORT</div>
      <div className="grid border-y-2 border-black" style={{ gridTemplateColumns: "1fr 120px 1fr" }}>
        <div>
          <div className="border-t border-b border-black text-center font-bold text-[13px] py-0.5">Patient Information</div>
          <div className="px-1.5 py-1 space-y-1">
            <KV k="Name" v={(p.name || "").toUpperCase()} />
            <KV k="Sex" v={`${p.age ?? "—"} / ${p.gender || "—"}`} />
            <KV k="UHID" v={p.uhid} />
            <KV k="Ref. By" v={r.doctor_name || "Self"} />
            {p.mobile && <KV k="Mobile No." v={p.mobile} />}
          </div>
        </div>
        <div className="border-x border-black border-t flex items-center justify-center p-1">
          <QRCodeSVG value={qr} size={84} level="M" data-testid="lab-report-qr" />
        </div>
        <div>
          <div className="border-t border-b border-black text-center font-bold text-[13px] py-0.5">Sample Information</div>
          <div className="px-1.5 py-1 space-y-1">
            <KV k={<b>Lab Id</b>} v={[...new Set(sections.map((s) => s.lab_no).filter(Boolean))].join(", ") || r.lab_no} />
            <KV k="Collected On" v={fmtDT(r.collected_at)} />
            <KV k="Reg. Date Time" v={fmtDT(r.ordered_at)} />
            <KV k="Report Date & Time" v={fmtDT(reportAt)} />
            <KV k="Sample Type" v={sampleTypes} />
          </div>
        </div>
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return <div className="flex gap-1.5"><span className="shrink-0">{k} :</span><span>{v || ""}</span></div>;
}

function Results({ s, patient, showHeading }) {
  const rows = buildRows({ ...s, patient });
  const heading = (s.template?.category || s.name || "").toUpperCase();
  let lastGroup = "";
  return (
    <div className="mt-2 text-[12.5px]" data-testid={`lab-report-results-${s.id}`}>
      {showHeading && <div className="lab-head text-center font-bold underline text-[13px] mb-2 mt-1">{heading}</div>}
      {s.template?.category && s.name && <div className="lab-head font-bold mb-1 pl-4">{s.name}</div>}
      <table className="w-full">
        <colgroup><col style={{ width: "42%" }} /><col style={{ width: "14%" }} /><col style={{ width: "13%" }} /><col /></colgroup>
        <tbody>
          {rows.length === 0 && !(s.findings || s.impression) && <tr><td colSpan={4} className="py-2 text-center italic text-slate-500">Result pending.</td></tr>}
          {rows.map((row, i) => {
            const groupRow = row.group && row.group !== lastGroup;
            lastGroup = row.group;
            return [
              groupRow && <tr key={`g${i}`} className="lab-row"><td colSpan={4} className="pl-4 pt-1 pb-0.5">{row.group}</td></tr>,
              <tr key={i} className="lab-row" data-testid={`lab-row-${s.id}-${i}`}>
                <td className={`${row.group ? "pl-10" : "pl-4"} py-[3px]`}>{row.name}</td>
                <td className={`py-[3px] ${row.flag ? "font-bold" : ""}`}>{row.flag ? `${row.flag} ` : ""}{row.result || "----"}</td>
                <td className="py-[3px]">{row.unit}</td>
                <td className="py-[3px]">{row.range}</td>
              </tr>,
            ];
          })}
        </tbody>
      </table>
      <Narrative r={s} />
    </div>
  );
}

function Narrative({ r }) {
  const items = [["Findings", r.findings], ["Impression", r.impression], ["Remarks", r.remarks]].filter(([, v]) => v);
  if (!items.length) return null;
  return (
    <div className="lab-narr mt-2 space-y-1 pl-4 text-[12px]">
      {items.map(([k, v]) => (
        <div key={k}><b>{k}:</b> <span className="whitespace-pre-wrap">{v}</span></div>
      ))}
    </div>
  );
}

function SignatureFooter({ r }) {
  const h = r.hospital || {};
  return (
    <div className="mt-6 break-inside-avoid" data-testid="lab-report-footer">
      <div className="border-t-2 border-black pt-1.5 text-[12px]">
        {h.lab_signature_url
          ? <img src={`${API_BASE}${h.lab_signature_url}`} alt="Signature" className="h-11 object-contain" data-testid="lab-report-signature" />
          : <div className="h-11" />}
        <div className="font-bold" data-testid="lab-report-signatory">{h.lab_signatory_name || r.verified_by_name || "Pathologist"}</div>
        <div>{h.lab_signatory_qual}</div>
        <div className="text-center text-[10.5px] mt-3 mb-1.5">This is an Electronically Authorized Report.</div>
      </div>
      <div className="border-t-2 border-black" />
    </div>
  );
}
